"use client"
import { useState, useEffect } from "react"
import { fetchCoachData, verifyCoach, updateCoach, addCoach } from "../comman/Coach.js"
import Sidebar from "../components/Sidebar.js"
import CoachModal from "../components/CoachModal.js"
import { FaEdit, FaTrash, FaBell,FaCheckCircle } from "react-icons/fa"
import { IoPersonAdd, IoSearch } from "react-icons/io5"
import NotificationModal from "../components/Notifications.js"

const CoachDetails = () => {
  const [coaches, setCoaches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [coachData, setCoachData] = useState(null)
  const [coachToNotification, setCoachToNotification] = useState(null)
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false)

  useEffect(() => {
    fetchCoachData()
      .then((data) => {
        console.log("Coach data fetched:", data)
        setCoaches(data.coaches)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  const handleNotification = (coach) => {
    setCoachToNotification(coach)
    setIsNotificationModalOpen(true)
  }

  const handleVerify = async (coachId) => {
    try {
      const response = await verifyCoach(coachId)
      setCoaches((prevCoaches) =>
        prevCoaches.map((coach) =>
          coach._id === coachId
            ? { ...coach, profile: { ...coach.profile, verify: response.verifyStatus } }
            : coach,
        ),
      )
    } catch (error) {
      console.error("Error verifying coach:", error)
      if (error.response && error.response.status === 400) {
        alert("This coach is already verified.")
      } else {
        alert("An error occurred while verifying the coach.")
      }
    }
  }

  const handleAddCoach = async (newCoach) => {
    const response = await addCoach(newCoach)
    setIsModalOpen(false)
    setCoaches((prev) => [...prev, response.newCoach])
  }

  const handleUpdateCoach = async (updatedCoach) => {
    try {
      await updateCoach(updatedCoach)
      const updatedData = await fetchCoachData()
      setCoaches(updatedData.coaches)
      setIsModalOpen(false)
    } catch (error) {
      console.error("Error updating coach:", error)
      alert("Failed to update the coach. Please try again.")
    }
  }

  const handleEditClick = (coach) => {
    setCoachData(coach)
    setIsModalOpen(true)
  }

  const handleAddClick = () => {
    setCoachData(null)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setIsNotificationModalOpen(false)
  }

  const filteredCoaches = coaches.filter(
    (coach) =>
      coach.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coach.email?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64">
        <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Coach Management</h1>
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative w-full md:w-64">
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                placeholder="Search coaches"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <IoSearch className="w-5 h-5 text-gray-500" />
              </div>
            </div>
            <button
              onClick={handleAddClick}
              className="flex items-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
            >
              <IoPersonAdd size={18} className="mr-2" />
              Add Coach
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-white uppercase bg-[#12143F]">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Phone Number
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Academy Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Designation
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Sport
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Has Academy
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Verify
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCoaches.length > 0 ? (
                  filteredCoaches.map((coach) => (
                    <tr key={coach._id} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">{coach?.name}</td>
                      <td className="px-6 py-4">{coach?.email}</td>
                      <td className="px-6 py-4">{coach?.phone}</td>
                      <td className="px-6 py-4">
                        {coach.teachesAtAcademyId && coach.teachesAtAcademyId.length > 0
                          ? coach.teachesAtAcademyId.map((academy) => academy.name).join(", ")
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4">{coach.role}</td>
                      <td className="px-6 py-4">{coach.profile?.sport || "N/A"}</td>
                      <td className="px-6 py-4">{coach.hasAcademy ? "Yes" : "No"}</td>
                      <td className="px-6 py-4">
  {coach.profile?.verify ? (
    <div className="flex items-center text-green-600">
      <FaCheckCircle className="w-4 h-4 mr-1.5" />
      <span className="font-medium">Verified</span>
    </div>
  ) : (
    <button
      className="px-4 py-1.5 bg-[#12143F] text-white rounded-md transition-all hover:bg-opacity-90 text-sm font-medium flex items-center"
      onClick={() => handleVerify(coach._id)}
    >
      <FaCheckCircle className="w-4 h-4 mr-1.5" />
      Verify Now
    </button>
  )}
</td>

                      <td className="px-6 py-4 space-x-3">
                        <button
                          onClick={() => handleNotification(coach)}
                          className="text-yellow-500 hover:text-yellow-600 transition-colors duration-200"
                        >
                          <FaBell size={18} />
                        </button>
                        <button
                          onClick={() => handleEditClick(coach)}
                          className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200"
                        >
                          <FaEdit size={18} />
                        </button>
                        <button
                          onClick={() => {
                            /* Implement delete functionality */
                          }}
                          className="text-red-500 hover:text-red-600 transition-colors duration-200"
                        >
                          <FaTrash size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                      No coaches present at the moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <CoachModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        coachData={coachData}
        handleAddCoach={handleAddCoach}
        handleUpdateCoach={handleUpdateCoach}
      />
      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={handleCloseModal}
        student={coachToNotification}
      />
    </div>
  )
}

export default CoachDetails